import { Component, inject, Input, input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { trackByIndexFunction } from "core/functions";
import { CardComponent } from "common/card/card.component";
import { NgClass } from "@angular/common";
import { ClickOutsideDirective } from "common/click-outside.directive";
import { SpinComponent } from "common/spin/spin.component";
import { TranslateModule } from "@ngx-translate/core";
import { DialogService } from "common/dialog/dialog.service";
import { ActivatedRoute } from "@angular/router";
import { AiAcknowledgementsPopupComponent } from "common/disclaimer/ai-acknowledgements/ai-acknowledgements-popup.component";
import AiAcknowledgementsState = stages.userservice.core.AiAcknowledgementsState;

@Component({
	selector: "stages-floating-action-menu",
	templateUrl: "floating-action-menu.component.html",
	styleUrls: ["floating-action-menu.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, ClickOutsideDirective, SpinComponent, TranslateModule],
})
export class FloatingActionMenuComponent implements OnInit {
	readonly buttonTitle = input<string>("navigation.workspace.addelement");
	readonly dialogService = inject(DialogService);
	readonly route = inject(ActivatedRoute);

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	menuItems: MenuItem[] = [];

	readonly iconClass = input<string>();

	readonly customButtonClasses = input<string[]>();

	readonly iconSize = input<string>("30px");

	readonly closeIcon = input<string>("ico ico-add");

	readonly isButtonDisabled = input<boolean>(false);

	readonly isLoading = input<boolean>(false);

	readonly darkTheme = input<boolean>(false);

	readonly card = input<CardComponent>();

	readonly mode = input<"button" | "menu">("menu");

	readonly aiAcknowledgmentsState = input<AiAcknowledgementsState>("ACCEPTED");

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set triggerCloseHandler(close: boolean) {
		if (close) {
			this.open = false;
			const card = this.card();
			if (card) {
				card.resetHeight();
			}
		}
	}

	private INITIAL_BUBBLE_VALUE = 70;
	private DISTANCE_BETWEEN_BUBBLES = 54;
	private UPPERMARGIN = this.DISTANCE_BETWEEN_BUBBLES;
	private DELAY = 0.05;
	open = false;

	closeHandler(): void {
		this.open = false;
		const card = this.card();
		if (card) {
			card.resetHeight();
		}
	}

	toggleMenu(): void {
		this.open = !this.open;
		const card = this.card();
		if (card?.expanded) {
			const maxHeight =
				this.INITIAL_BUBBLE_VALUE + this.UPPERMARGIN + this.menuItems.length * this.DISTANCE_BETWEEN_BUBBLES;
			if (this.open && card.getHeight() < maxHeight) {
				card.setHeight(maxHeight.toString() + "px");
			} else {
				card.resetHeight();
			}
		}
	}

	getTransitionDelay(index: number): string {
		const delay = this.DELAY * index;
		return delay.toString() + "s";
	}

	trackByIndex = trackByIndexFunction;

	ngOnInit(): void {
		const card = this.card();
		if (card) {
			card.activateTransition();
		}
	}

	async showAcknowledgements(): Promise<void> {
		const result = await this.dialogService.open("aiAcknowledgements", {
			component: AiAcknowledgementsPopupComponent,
			componentArgs: {
				processVersion: this.route.snapshot.paramMap.get("processVersion"),
			},
			defaultResult: "NONE",
			dialogConfig: {
				size: "large",
				autoHeight: true,
			},
		});
		if (result === "ACCEPTED") {
			this.toggleMenu();
		}
	}
}
